import React, { useState } from 'react';
import { Card, Table, Button, Space, Tag, Modal, Form, Input, DatePicker, InputNumber, message, Descriptions, Alert, Steps, Select } from 'antd';
import { PlusOutlined, SafetyCertificateOutlined, FileTextOutlined, SendOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useActiveCustomers } from '../../hooks/useCustomers';

const { Step } = Steps;
const { Option } = Select;

interface ZATCAInvoice {
  id: string;
  invoice_number: string;
  invoice_date: string;
  customer_name: string;
  customer_vat_number: string;
  total_amount: number;
  vat_amount: number;
  net_amount: number;
  invoice_hash: string;
  cryptographic_stamp: string;
  uuid: string;
  zatca_phase: 'Phase-1' | 'Phase-2';
  clearance_status?: 'Pending' | 'Cleared' | 'Rejected';
  zatca_invoice_number?: string;
  submitted_date?: string;
}

const KSAZATCAPage: React.FC = () => {
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<ZATCAInvoice | null>(null);
  const [loading, setLoading] = useState(false);
  const { data: customers, isLoading: isLoadingCustomers } = useActiveCustomers();

  // Mock data
  const [invoices, setInvoices] = useState<ZATCAInvoice[]>([
    {
      id: '1',
      invoice_number: 'INV-KSA-2025-001',
      invoice_date: '2025-01-15',
      customer_name: 'Saudi Business Company',
      customer_vat_number: '300012345678903',
      total_amount: 11500,
      vat_amount: 1500,
      net_amount: 10000,
      invoice_hash: 'a3f7b2e9c8d5e1f4a6b9c2d7e8f3a1b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0',
      cryptographic_stamp: 'MEUCIQDx5tKzN+VrR4h3sM8p2wQ7kLvF6nS9dT1eH3gJ4mL8xQIgY2nR5wT8vK3pL4qM7sN9jF2gH6kT1eD4bC8xZ5yP3rA=',
      uuid: '550e8400-e29b-41d4-a716-446655440000',
      zatca_phase: 'Phase-2',
      clearance_status: 'Cleared',
      zatca_invoice_number: 'ZATCA-2025-001',
      submitted_date: '2025-01-15 10:30:00',
    },
  ]);

  const columns: ColumnsType<ZATCAInvoice> = [
    {
      title: 'Invoice Number',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      width: 180,
    },
    {
      title: 'Date',
      dataIndex: 'invoice_date',
      key: 'invoice_date',
      width: 120,
      render: (date: string) => dayjs(date).format('DD-MMM-YYYY'),
    },
    {
      title: 'Customer Name',
      dataIndex: 'customer_name',
      key: 'customer_name',
      width: 200,
    },
    {
      title: 'VAT Number',
      dataIndex: 'customer_vat_number',
      key: 'customer_vat_number',
      width: 150,
    },
    {
      title: 'Net Amount',
      dataIndex: 'net_amount',
      key: 'net_amount',
      width: 120,
      align: 'right',
      render: (amount: number) => `SAR ${amount.toFixed(2)}`,
    },
    {
      title: 'VAT (15%)',
      dataIndex: 'vat_amount',
      key: 'vat_amount',
      width: 120,
      align: 'right',
      render: (amount: number) => `SAR ${amount.toFixed(2)}`,
    },
    {
      title: 'Total',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 120,
      align: 'right',
      render: (amount: number) => <strong>SAR {amount.toFixed(2)}</strong>,
    },
    {
      title: 'Phase',
      dataIndex: 'zatca_phase',
      key: 'zatca_phase',
      width: 100,
      render: (phase: string) => (
        <Tag color={phase === 'Phase-2' ? 'blue' : 'default'}>{phase}</Tag>
      ),
    },
    {
      title: 'Clearance Status',
      dataIndex: 'clearance_status',
      key: 'clearance_status',
      width: 140,
      render: (status?: string) => {
        if (!status) return <Tag>Not Submitted</Tag>;
        const colorMap: Record<string, string> = {
          Pending: 'orange',
          Cleared: 'green',
          Rejected: 'red',
        };
        const iconMap: Record<string, React.ReactNode> = {
          Pending: <ClockCircleOutlined />,
          Cleared: <CheckCircleOutlined />,
          Rejected: <CheckCircleOutlined />,
        };
        return (
          <Tag color={colorMap[status]} icon={iconMap[status]}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 200,
      render: (_: any, record: ZATCAInvoice) => (
        <Space size="small">
          <Button
            type="text"
            icon={<FileTextOutlined />}
            size="small"
            onClick={() => handleViewDetails(record)}
          >
            Details
          </Button>
          {!record.clearance_status && (
            <Button
              type="primary"
              size="small"
              icon={<SendOutlined />}
              onClick={() => handleSubmitToZATCA(record)}
            >
              Submit
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const handleViewDetails = (invoice: ZATCAInvoice) => {
    setSelectedInvoice(invoice);
    setDetailsModalVisible(true);
  };

  const handleSubmitToZATCA = (invoice: ZATCAInvoice) => {
    Modal.confirm({
      title: 'Submit to ZATCA',
      content: `Are you sure you want to submit invoice ${invoice.invoice_number} to ZATCA for clearance?`,
      okText: 'Submit',
      cancelText: 'Cancel',
      onOk: async () => {
        // Simulate API call
        message.loading({ content: 'Submitting to ZATCA...', key: 'zatca' });
        await new Promise(resolve => setTimeout(resolve, 2000));

        const updatedInvoice = {
          ...invoice,
          clearance_status: 'Cleared' as const,
          zatca_invoice_number: `ZATCA-${dayjs().format('YYYY')}-${String(invoices.length + 1).padStart(3, '0')}`,
          submitted_date: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        };
        setInvoices(invoices.map(inv => inv.id === invoice.id ? updatedInvoice : inv));
        message.success({ content: 'Invoice cleared by ZATCA successfully', key: 'zatca' });
      },
    });
  };

  const handleGenerate = () => {
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Simulate API call to generate ZATCA-compliant invoice
      await new Promise(resolve => setTimeout(resolve, 2000));

      const vatAmount = values.net_amount * 0.15;
      const totalAmount = values.net_amount + vatAmount;

      // Generate mock hash and cryptographic stamp
      const invoiceData = `${values.customer_name}|${values.customer_vat_number}|${dayjs().format('YYYY-MM-DD')}|${totalAmount}`;
      const mockHash = Array.from({ length: 64 }, () => 
        Math.floor(Math.random() * 16).toString(16)
      ).join('');
      const mockStamp = btoa(Array.from({ length: 64 }, () => 
        String.fromCharCode(Math.floor(Math.random() * 26) + 65)
      ).join(''));

      const newInvoice: ZATCAInvoice = {
        id: Date.now().toString(),
        invoice_number: `INV-KSA-${dayjs().format('YYYY')}-${String(invoices.length + 1).padStart(3, '0')}`,
        invoice_date: values.invoice_date.format('YYYY-MM-DD'),
        customer_name: values.customer_name,
        customer_vat_number: values.customer_vat_number,
        total_amount: totalAmount,
        vat_amount: vatAmount,
        net_amount: values.net_amount,
        invoice_hash: mockHash,
        cryptographic_stamp: mockStamp,
        uuid: crypto.randomUUID(),
        zatca_phase: 'Phase-2',
      };

      setInvoices([newInvoice, ...invoices]);
      message.success('ZATCA Phase-2 compliant e-invoice generated successfully');
      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  return (
    <div style={{ padding: '24px' }}>
      <Alert
        message="KSA ZATCA Phase-2 E-Invoicing"
        description="This module generates ZATCA Phase-2 compliant electronic invoices with cryptographic stamps, invoice hashing, and real-time clearance. All invoices are digitally signed and validated by ZATCA before issuance."
        type="info"
        showIcon
        icon={<SafetyCertificateOutlined />}
        style={{ marginBottom: 24 }}
      />

      <Card
        title="ZATCA Phase-2 E-Invoices"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleGenerate}>
            Generate E-Invoice
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={invoices}
          rowKey="id"
          scroll={{ x: 1600 }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} e-invoices`,
          }}
        />
      </Card>

      <Modal
        title="Generate ZATCA Phase-2 E-Invoice"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={loading}
        width={600}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 20 }}>
          <Form.Item
            name="customer_name"
            label="Customer Name"
            rules={[{ required: true, message: 'Please select customer' }]}
          >
            <Select
              showSearch
              placeholder="Select customer"
              loading={isLoadingCustomers}
              optionFilterProp="children"
              filterOption={(input, option) =>
                String(option?.label ?? option?.children ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {customers?.map((customer) => (
                <Option key={customer.id} value={customer.customer_name}>
                  {customer.customer_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="customer_vat_number"
            label="Customer VAT Number"
            rules={[
              { required: true, message: 'Please enter customer VAT number' },
              { pattern: /^\d{15}$/, message: 'VAT number must be 15 digits' },
            ]}
          >
            <Input placeholder="Enter 15-digit VAT number" maxLength={15} />
          </Form.Item>

          <Form.Item
            name="invoice_date"
            label="Invoice Date"
            rules={[{ required: true, message: 'Please select invoice date' }]}
            initialValue={dayjs()}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="net_amount"
            label="Net Amount (SAR)"
            rules={[{ required: true, message: 'Please enter net amount' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              step={0.01}
              precision={2}
              placeholder="Enter net amount"
            />
          </Form.Item>

          <Alert
            message="ZATCA Phase-2 Features"
            description="• 15% VAT auto-calculation • Cryptographic digital signature • SHA-256 invoice hashing • UUID generation • Real-time ZATCA clearance"
            type="success"
            showIcon
            style={{ marginTop: 10 }}
          />
        </Form>
      </Modal>

      <Modal
        title="ZATCA E-Invoice Details"
        open={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailsModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
      >
        {selectedInvoice && (
          <div style={{ marginTop: 20 }}>
            <Steps current={selectedInvoice.clearance_status === 'Cleared' ? 3 : selectedInvoice.clearance_status ? 2 : 1} size="small" style={{ marginBottom: 30 }}>
              <Step title="Generated" icon={<CheckCircleOutlined />} />
              <Step title="Signed" icon={<CheckCircleOutlined />} />
              <Step title="Submitted" icon={selectedInvoice.clearance_status ? <CheckCircleOutlined /> : <ClockCircleOutlined />} />
              <Step title="Cleared" icon={selectedInvoice.clearance_status === 'Cleared' ? <CheckCircleOutlined /> : <ClockCircleOutlined />} />
            </Steps>

            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Invoice Number">{selectedInvoice.invoice_number}</Descriptions.Item>
              <Descriptions.Item label="UUID">{selectedInvoice.uuid}</Descriptions.Item>
              <Descriptions.Item label="Invoice Date">{dayjs(selectedInvoice.invoice_date).format('DD-MMM-YYYY')}</Descriptions.Item>
              <Descriptions.Item label="Customer Name">{selectedInvoice.customer_name}</Descriptions.Item>
              <Descriptions.Item label="Customer VAT Number">{selectedInvoice.customer_vat_number}</Descriptions.Item>
              <Descriptions.Item label="Net Amount">SAR {selectedInvoice.net_amount.toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="VAT Amount (15%)">SAR {selectedInvoice.vat_amount.toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="Total Amount"><strong>SAR {selectedInvoice.total_amount.toFixed(2)}</strong></Descriptions.Item>
              <Descriptions.Item label="ZATCA Phase">
                <Tag color="blue">{selectedInvoice.zatca_phase}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Clearance Status">
                {selectedInvoice.clearance_status ? (
                  <Tag color={selectedInvoice.clearance_status === 'Cleared' ? 'green' : selectedInvoice.clearance_status === 'Pending' ? 'orange' : 'red'}>
                    {selectedInvoice.clearance_status}
                  </Tag>
                ) : (
                  <Tag>Not Submitted</Tag>
                )}
              </Descriptions.Item>
              {selectedInvoice.zatca_invoice_number && (
                <Descriptions.Item label="ZATCA Invoice Number">{selectedInvoice.zatca_invoice_number}</Descriptions.Item>
              )}
              {selectedInvoice.submitted_date && (
                <Descriptions.Item label="Submitted Date">{selectedInvoice.submitted_date}</Descriptions.Item>
              )}
              <Descriptions.Item label="Invoice Hash (SHA-256)">
                <div style={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '12px' }}>
                  {selectedInvoice.invoice_hash}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Cryptographic Stamp">
                <div style={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '12px' }}>
                  {selectedInvoice.cryptographic_stamp}
                </div>
              </Descriptions.Item>
            </Descriptions>

            <Alert
              message="ZATCA Phase-2 Compliance"
              description="This invoice includes cryptographic digital signature, SHA-256 hash, and meets all ZATCA Phase-2 requirements for real-time invoice clearance."
              type="success"
              showIcon
              icon={<SafetyCertificateOutlined />}
              style={{ marginTop: 20 }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default KSAZATCAPage;


